import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent } from './ui/card';
import { Alert, AlertDescription } from './ui/alert';
import { Calculator, Upload, Check, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

// EmailJS Configuration
const EMAILJS_PUBLIC_KEY = "CPpYWKJXZXsZfLSX7";
const EMAILJS_SERVICE_ID = "service_gbk1k0x";
const EMAILJS_TEMPLATE_ID_CLIENT = "template_v2116tq";
const EMAILJS_TEMPLATE_ID_ADMIN = "template_42xqw5d";

// Updated Pricing Tiers
const PRICING_TIERS = [
  { min: 1, max: 50, price: 500, label: '1-50 transactions' },
  { min: 51, max: 100, price: 800, label: '51-100 transactions' },
  { min: 101, max: 200, price: 1200, label: '101-200 transactions' },
  { min: 201, max: 350, price: 1800, label: '201-350 transactions' },
  { min: 351, max: 500, price: 2500, label: '351-500 transactions' },
  { min: 501, max: Infinity, price: 3500, label: '500+ transactions' }
];

const ADDITIONAL_SERVICES = {
  corporateTaxReg: { name: 'Corporate Tax Registration', price: 499 },
  vatReg: { name: 'VAT Registration', price: 499 },
  corporateTaxReturn: { name: 'Corporate Tax Return Filing', price: 799 },
  taxResidency: { name: 'Tax Residency Certificate', price: 1999 }
};

export const PriceCalculator = () => {
  const [step, setStep] = useState(1);
  const [isCalculating, setIsCalculating] = useState(false);
  const [pricingResult, setPricingResult] = useState(null);
  const [formData, setFormData] = useState({
    fullName: '',
    companyName: '',
    email: '',
    mobile: '',
    businessType: '',
    monthsCovered: 1,
    transactionCount: '',
    corporateTaxReg: false,
    vatReg: false,
    corporateTaxReturn: false,
    taxResidency: false,
    bankStatement: null
  });

  // Load EmailJS
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/@emailjs/browser@3/dist/email.min.js';
    script.async = true;
    script.onload = () => {
      if (window.emailjs) {
        window.emailjs.init(EMAILJS_PUBLIC_KEY);
      }
    };
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.toLowerCase().endsWith('.pdf')) {
        toast.error('Only PDF files are allowed');
        e.target.value = '';
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File size must be less than 10MB');
        e.target.value = '';
        return;
      }
      setFormData(prev => ({ ...prev, bankStatement: file }));
      toast.success('PDF uploaded successfully');
    }
  };

  const validateStep1 = () => {
    if (!formData.fullName || !formData.companyName || !formData.email || !formData.mobile || !formData.businessType) {
      toast.error('Please fill in all required fields');
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    if (!formData.bankStatement) {
      toast.error('Please upload your bank statement');
      return false;
    }
    if (!formData.transactionCount || formData.transactionCount < 1) {
      toast.error('Please enter the number of transactions');
      return false;
    }
    if (formData.monthsCovered < 1 || formData.monthsCovered > 24) {
      toast.error('Months covered must be between 1 and 24');
      return false;
    }
    return true;
  };

  const getPricingTier = (transactionCount) => {
    for (const tier of PRICING_TIERS) {
      if (transactionCount >= tier.min && transactionCount <= tier.max) {
        return tier;
      }
    }
    return PRICING_TIERS[PRICING_TIERS.length - 1];
  };

  const calculatePricing = (transactionCount) => {
    const tier = getPricingTier(transactionCount);
    const monthlyPrice = tier.price;
    const bookkeepingTotal = monthlyPrice * formData.monthsCovered;

    let additionalServices = [];
    let additionalTotal = 0;

    if (formData.corporateTaxReg) {
      additionalServices.push(ADDITIONAL_SERVICES.corporateTaxReg);
      additionalTotal += ADDITIONAL_SERVICES.corporateTaxReg.price;
    }
    if (formData.vatReg) {
      additionalServices.push(ADDITIONAL_SERVICES.vatReg);
      additionalTotal += ADDITIONAL_SERVICES.vatReg.price;
    }
    if (formData.corporateTaxReturn) {
      additionalServices.push(ADDITIONAL_SERVICES.corporateTaxReturn);
      additionalTotal += ADDITIONAL_SERVICES.corporateTaxReturn.price;
    }
    if (formData.taxResidency) {
      additionalServices.push(ADDITIONAL_SERVICES.taxResidency);
      additionalTotal += ADDITIONAL_SERVICES.taxResidency.price;
    }

    const total = bookkeepingTotal + additionalTotal;

    return {
      transactionCount,
      tier: tier.label,
      monthlyPrice,
      monthsCovered: formData.monthsCovered,
      bookkeepingTotal,
      additionalServices,
      additionalTotal,
      total,
      currency: 'AED'
    };
  };

  const sendEmails = async (pricing) => {
    if (!window.emailjs) {
      console.warn('EmailJS not loaded');
      return;
    }

    // Send both emails independently using Promise.allSettled
    // This ensures admin email is sent even if client email fails
    const emailPromises = [
      // Email to client
      (async () => {
        try {
          // Generate quote reference number
          const quoteRef = `SHN-${Date.now().toString().slice(-8)}`;
          
          // Format services list
          const servicesList = pricing.additionalServices.length > 0
            ? pricing.additionalServices.map(s => `${s.name}: AED ${s.price}`).join('\n')
            : 'None';
          
          const clientParams = {
            to_email: formData.email,              // Required for recipient address
            client_name: formData.fullName,        // Template variable: {{client_name}}
            company_name: formData.companyName,    // Template variable: {{company_name}}
            client_email: formData.email,          // Template variable: {{client_email}}
            quote_date: new Date().toLocaleDateString('en-GB'),  // Template variable: {{quote_date}}
            quote_ref: quoteRef,                   // Template variable: {{quote_ref}}
            services_list: servicesList,           // Template variable: {{services_list}}
            transaction_count: pricing.transactionCount,  // Template variable: {{transaction_count}}
            months_covered: pricing.monthsCovered, // Template variable: {{months_covered}}
            bookkeeping_price: pricing.bookkeepingTotal,  // Template variable: {{bookkeeping_price}}
            addons_price: pricing.additionalTotal, // Template variable: {{addons_price}}
            total_price: pricing.total             // Template variable: {{total_price}}
          };

          console.log('📧 Sending client email with params:', clientParams);

          await window.emailjs.send(
            EMAILJS_SERVICE_ID,
            EMAILJS_TEMPLATE_ID_CLIENT,
            clientParams
          );
          
          console.log('✅ Client email sent successfully');
          return { type: 'client', status: 'success' };
        } catch (error) {
          console.error('❌ Client email failed with error:', error);
          console.error('Error details:', {
            status: error.status,
            text: error.text,
            message: error.message
          });
          return { type: 'client', status: 'failed', error };
        }
      })(),

      // Email to admin (info@shineaccounting.co)
      (async () => {
        try {
          const adminParams = {
            to_email: 'info@shineaccounting.co',
            client_name: formData.fullName,
            company_name: formData.companyName,
            client_email: formData.email,
            phone_number: formData.mobile,
            business_type: formData.businessType,
            pdf_filename: formData.bankStatement?.name || 'N/A',
            transaction_count: pricing.transactionCount,
            tier: pricing.tier,
            months_covered: formData.monthsCovered,
            selected_services: pricing.additionalServices.map(s => s.name).join(', ') || 'None',
            total_quote: `${pricing.currency} ${pricing.total.toLocaleString()}`,
            submission_date: new Date().toLocaleDateString('en-GB')
          };

          console.log('📧 Sending admin email with params:', adminParams);

          await window.emailjs.send(
            EMAILJS_SERVICE_ID,
            EMAILJS_TEMPLATE_ID_ADMIN,
            adminParams
          );
          
          console.log('✅ Admin email sent successfully');
          return { type: 'admin', status: 'success' };
        } catch (error) {
          console.error('❌ Admin email failed with error:', error);
          console.error('Error details:', {
            status: error.status,
            text: error.text,
            message: error.message
          });
          return { type: 'admin', status: 'failed', error };
        }
      })()
    ];

    const results = await Promise.allSettled(emailPromises);
    
    // Check results and show appropriate message
    const clientResult = results[0].status === 'fulfilled' ? results[0].value : { status: 'failed' };
    const adminResult = results[1].status === 'fulfilled' ? results[1].value : { status: 'failed' };
    
    if (clientResult.status === 'success' && adminResult.status === 'success') {
      toast.success('Quote sent successfully to both client and admin!');
    } else if (adminResult.status === 'success') {
      toast.warning('Admin notified, but client email failed. Please contact us directly for your quote.');
    } else if (clientResult.status === 'success') {
      toast.warning('Quote sent to you, but admin notification failed. We will follow up shortly.');
    } else {
      toast.warning('Quote calculated but email notifications failed. Please contact us directly at info@shineaccounting.co');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateStep2()) return;
    
    setIsCalculating(true);
    
    try {
      // Calculate pricing directly on frontend with new tiers
      const transactionCount = parseInt(formData.transactionCount);
      const pricing = calculatePricing(transactionCount);
      setPricingResult(pricing);
      
      // Send dual emails via EmailJS
      await sendEmails(pricing);
      
      // Track Google Ads conversion for price calculator submission
      if (window.gtag) {
        window.gtag('event', 'conversion', {
          'send_to': 'AW-18232866587/fZGRCMuqtb0cEJvujfZD'
        });
      }
      
      setStep(3);
      toast.success('Price calculated successfully!');
      
    } catch (error) {
      console.error('Price Calculator Error:', error);
      toast.error('Failed to calculate price. Please try again.');
    } finally {
      setIsCalculating(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED',
      minimumFractionDigits: 0
    }).format(amount);
  };

  return (
    <section id="price-calculator" className="py-24 bg-gradient-to-b from-white to-muted/30">
      <div className="container mx-auto px-6">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-accent/10 text-accent mb-6">
            <Calculator size={32} />
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-primary mb-6">
            Price Calculator
          </h2>
          <p className="text-lg text-muted-foreground">
            Get an instant estimate for our accounting and compliance services based on your transaction volume
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          {/* Progress Steps */}
          <div className="flex justify-center items-center mb-12">
            <div className="flex items-center gap-4">
              <div className={`flex items-center justify-center w-10 h-10 rounded-full font-bold transition-all ${
                step >= 1 ? 'bg-accent text-accent-foreground' : 'bg-gray-200 text-gray-500'
              }`}>
                {step > 1 ? <Check size={20} /> : '1'}
              </div>
              <div className={`h-1 w-20 ${step >= 2 ? 'bg-accent' : 'bg-gray-200'}`}></div>
              <div className={`flex items-center justify-center w-10 h-10 rounded-full font-bold transition-all ${
                step >= 2 ? 'bg-accent text-accent-foreground' : 'bg-gray-200 text-gray-500'
              }`}>
                {step > 2 ? <Check size={20} /> : '2'}
              </div>
              <div className={`h-1 w-20 ${step >= 3 ? 'bg-accent' : 'bg-gray-200'}`}></div>
              <div className={`flex items-center justify-center w-10 h-10 rounded-full font-bold transition-all ${
                step >= 3 ? 'bg-accent text-accent-foreground' : 'bg-gray-200 text-gray-500'
              }`}>
                3
              </div>
            </div>
          </div>

          <Card className="border-2">
            <CardContent className="p-8">
              <form onSubmit={handleSubmit}>
                {/* Step 1: Client Details */}
                {step === 1 && (
                  <div className="space-y-6">
                    <h3 className="text-2xl font-bold text-primary mb-6">Step 1: Your Details</h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="fullName">Full Name *</Label>
                        <Input
                          id="fullName"
                          name="fullName"
                          value={formData.fullName}
                          onChange={handleInputChange}
                          placeholder="John Doe"
                          required
                          className="mt-2"
                        />
                      </div>

                      <div>
                        <Label htmlFor="companyName">Company Name *</Label>
                        <Input
                          id="companyName"
                          name="companyName"
                          value={formData.companyName}
                          onChange={handleInputChange}
                          placeholder="Your Company LLC"
                          required
                          className="mt-2"
                        />
                      </div>

                      <div>
                        <Label htmlFor="email">Email Address *</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          placeholder="you@company.com"
                          required
                          className="mt-2"
                        />
                      </div>

                      <div>
                        <Label htmlFor="mobile">Mobile Number *</Label>
                        <Input
                          id="mobile"
                          name="mobile"
                          type="tel"
                          value={formData.mobile}
                          onChange={handleInputChange}
                          placeholder="+971 50 123 4567"
                          required
                          className="mt-2"
                        />
                      </div>

                      <div className="md:col-span-2">
                        <Label htmlFor="businessType">Business Type *</Label>
                        <Input
                          id="businessType"
                          name="businessType"
                          value={formData.businessType}
                          onChange={handleInputChange}
                          placeholder="e.g., Trading, Consulting, E-commerce"
                          required
                          className="mt-2"
                        />
                      </div>
                    </div>

                    <Button
                      type="button"
                      onClick={() => {
                        if (validateStep1()) setStep(2);
                      }}
                      className="w-full bg-accent hover:bg-accent/90 text-accent-foreground font-semibold"
                      size="lg"
                    >
                      Next: Upload Bank Statement
                    </Button>
                  </div>
                )}

                {/* Step 2: Upload & Services */}
                {step === 2 && (
                  <div className="space-y-6">
                    <h3 className="text-2xl font-bold text-primary mb-6">Step 2: Bank Statement & Services</h3>
                    
                    <div>
                      <Label htmlFor="bankStatement">Upload Bank Statement (PDF) *</Label>
                      <div className="mt-2 flex items-center gap-4">
                        <input
                          id="bankStatement"
                          name="bankStatement"
                          type="file"
                          accept=".pdf"
                          onChange={handleFileChange}
                          className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-accent file:text-accent-foreground hover:file:bg-accent/90"
                          required
                        />
                        {formData.bankStatement && (
                          <Check className="text-green-600" size={24} />
                        )}
                      </div>
                      <Alert className="mt-3 border-blue-200 bg-blue-50">
                        <AlertCircle className="h-4 w-4 text-blue-600" />
                        <AlertDescription className="text-sm text-blue-800">
                          *Uploaded bank statements will be used strictly for transaction analysis and pricing calculation only. Documents will be deleted after review/calculation and will not be stored publicly or shared with any third party.
                        </AlertDescription>
                      </Alert>
                    </div>

                    <div>
                      <Label htmlFor="transactionCount">Number of Transactions *</Label>
                      <Input
                        id="transactionCount"
                        name="transactionCount"
                        type="number"
                        min="1"
                        value={formData.transactionCount}
                        onChange={handleInputChange}
                        placeholder="e.g., 75"
                        className="mt-2"
                        required
                      />
                      <p className="text-sm text-muted-foreground mt-1">
                        Enter the total number of transactions in your bank statement
                      </p>
                    </div>

                    <div>
                      <Label htmlFor="monthsCovered">Number of Months Covered *</Label>
                      <Input
                        id="monthsCovered"
                        name="monthsCovered"
                        type="number"
                        min="1"
                        max="24"
                        value={formData.monthsCovered}
                        onChange={handleInputChange}
                        className="mt-2"
                        required
                      />
                    </div>

                    <div className="border-t pt-6">
                      <Label className="text-lg font-semibold mb-4 block">Additional Services</Label>
                      <div className="space-y-3">
                        <div className="flex items-center space-x-3">
                          <input
                            type="checkbox"
                            id="corporateTaxReg"
                            name="corporateTaxReg"
                            checked={formData.corporateTaxReg}
                            onChange={handleInputChange}
                            className="w-5 h-5 text-accent rounded focus:ring-accent"
                          />
                          <label htmlFor="corporateTaxReg" className="text-sm font-medium cursor-pointer">
                            Corporate Tax Registration
                          </label>
                        </div>

                        <div className="flex items-center space-x-3">
                          <input
                            type="checkbox"
                            id="vatReg"
                            name="vatReg"
                            checked={formData.vatReg}
                            onChange={handleInputChange}
                            className="w-5 h-5 text-accent rounded focus:ring-accent"
                          />
                          <label htmlFor="vatReg" className="text-sm font-medium cursor-pointer">
                            VAT Registration
                          </label>
                        </div>

                        <div className="flex items-center space-x-3">
                          <input
                            type="checkbox"
                            id="corporateTaxReturn"
                            name="corporateTaxReturn"
                            checked={formData.corporateTaxReturn}
                            onChange={handleInputChange}
                            className="w-5 h-5 text-accent rounded focus:ring-accent"
                          />
                          <label htmlFor="corporateTaxReturn" className="text-sm font-medium cursor-pointer">
                            Corporate Tax Return Filing
                          </label>
                        </div>

                        <div className="flex items-center space-x-3">
                          <input
                            type="checkbox"
                            id="taxResidency"
                            name="taxResidency"
                            checked={formData.taxResidency}
                            onChange={handleInputChange}
                            className="w-5 h-5 text-accent rounded focus:ring-accent"
                          />
                          <label htmlFor="taxResidency" className="text-sm font-medium cursor-pointer">
                            Tax Residency Certificate
                          </label>
                        </div>
                      </div>
                    </div>

                    <div className="flex gap-4">
                      <Button
                        type="button"
                        onClick={() => setStep(1)}
                        variant="outline"
                        className="flex-1"
                        size="lg"
                      >
                        Back
                      </Button>
                      <Button
                        type="submit"
                        disabled={isCalculating}
                        className="flex-1 bg-accent hover:bg-accent/90 text-accent-foreground font-semibold"
                        size="lg"
                      >
                        {isCalculating ? (
                          <>
                            <span className="animate-spin mr-2">⏳</span>
                            Calculating...
                          </>
                        ) : (
                          'Calculate Price'
                        )}
                      </Button>
                    </div>
                  </div>
                )}

                {/* Step 3: Results */}
                {step === 3 && pricingResult && (
                  <div className="space-y-6">
                    <div className="text-center mb-8">
                      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 text-green-600 mb-4">
                        <Check size={32} />
                      </div>
                      <h3 className="text-3xl font-bold text-primary mb-2">Your Estimated Quote</h3>
                      <p className="text-muted-foreground">Based on your transaction analysis</p>
                    </div>

                    {/* Transaction Analysis */}
                    <Card className="bg-blue-50 border-blue-200">
                      <CardContent className="p-6">
                        <h4 className="font-bold text-lg mb-3 text-primary">Transaction Analysis</h4>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="text-muted-foreground">Total Transactions:</span>
                            <span className="font-bold ml-2">{pricingResult.transactionCount}</span>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Pricing Tier:</span>
                            <span className="font-bold ml-2">{pricingResult.tier}</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Pricing Breakdown */}
                    <div className="space-y-4">
                      <div className="flex justify-between items-center p-4 bg-gray-50 rounded-lg">
                        <div>
                          <div className="font-semibold">Bookkeeping / Bank Statement Analysis</div>
                          <div className="text-sm text-muted-foreground">
                            {formatCurrency(pricingResult.monthlyPrice)}/month × {pricingResult.monthsCovered} months
                          </div>
                        </div>
                        <div className="text-xl font-bold text-primary">
                          {formatCurrency(pricingResult.bookkeepingTotal)}
                        </div>
                      </div>

                      {pricingResult.additionalServices && pricingResult.additionalServices.length > 0 && (
                        <div className="space-y-2">
                          <div className="font-semibold text-lg">Additional Services</div>
                          {pricingResult.additionalServices.map((service, idx) => (
                            <div key={idx} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                              <div className="text-sm">{service.name}</div>
                              <div className="font-semibold">{formatCurrency(service.price)}</div>
                            </div>
                          ))}
                        </div>
                      )}

                      <div className="border-t-2 border-primary pt-4 mt-6">
                        <div className="flex justify-between items-center">
                          <div className="text-2xl font-bold text-primary">Total Estimated Quote</div>
                          <div className="text-3xl font-bold text-accent">
                            {formatCurrency(pricingResult.total)}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Disclaimer */}
                    <Alert className="border-yellow-200 bg-yellow-50">
                      <AlertCircle className="h-4 w-4 text-yellow-600" />
                      <AlertDescription className="text-sm text-yellow-800">
                        This is an estimated quote based on the information provided. Final pricing may vary after detailed review of your requirements and documentation.
                      </AlertDescription>
                    </Alert>

                    <div className="text-center pt-6">
                      <p className="text-sm text-muted-foreground mb-4">
                        A detailed quote has been sent to <strong>{formData.email}</strong>
                      </p>
                      <Button
                        type="button"
                        onClick={() => {
                          setStep(1);
                          setPricingResult(null);
                          setFormData({
                            fullName: '',
                            companyName: '',
                            email: '',
                            mobile: '',
                            businessType: '',
                            monthsCovered: 1,
                            transactionCount: '',
                            corporateTaxReg: false,
                            vatReg: false,
                            corporateTaxReturn: false,
                            taxResidency: false,
                            bankStatement: null
                          });
                          // Clear file input
                          const fileInput = document.getElementById('bankStatement');
                          if (fileInput) fileInput.value = '';
                        }}
                        variant="outline"
                        className="mr-4"
                      >
                        Calculate Another Quote
                      </Button>
                      <Button
                        type="button"
                        onClick={() => window.location.href = '#contact'}
                        className="bg-accent hover:bg-accent/90 text-accent-foreground"
                      >
                        Book Consultation
                      </Button>
                    </div>
                  </div>
                )}
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};